import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

import { BrandContext } from "../../../../contexts/BrandContext/context";
import { ImageUploader } from "../../../../components/ImageUploader/ImageUploader";
import { UserAvatar } from "../../../../components/common/UserAvatar";
import { storageService } from "../../../../services/storage.service";
import { brandService } from "../../../../services/brand.service";
import { passwordService } from "../../../../services/auth/password.service";
import { countries } from "../../../../constants/countries";
import { ImageCropModal } from "../../../../components/ImageCropModal/ImageCropModal";
import { readFile } from "../../../../utils/canvasUtils";
import { useDriver } from "@/contexts/driver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import {
  AsideRow,
  Field,
  FieldRow,
  SettingsGrid,
  SettingsSection,
  UnsavedBar,
  useSections,
} from "@/components/settings/settings-kit";
import { Flag } from "@/pages/brand/dashboard/affiliate-map";
import { formatDate } from "@/utils/formatters";

const TIMEZONES: { value: string; label: string }[] = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Central European Time" },
  { value: "Asia/Dubai", label: "Gulf Standard Time" },
  { value: "Asia/Singapore", label: "Singapore Time" },
];

/** "4:12 pm" in a zone, or null when the zone is unknown to the browser. */
function timeIn(zone: string, at: Date) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: zone,
    })
      .format(at)
      .toLowerCase();
  } catch {
    return null;
  }
}

/** The zone the browser reports, e.g. "Africa/Addis_Ababa". */
function deviceZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function zoneLabel(zone: string) {
  return TIMEZONES.find((t) => t.value === zone)?.label ?? zone.replace(/_/g, " ");
}

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  timezone: string;
  profileImage: string;
}

function DetailsSkeleton() {
  return (
    <SettingsGrid
      form={
        <>
          {[2, 2, 2].map((fields, i) => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="px-5 pt-4 pb-3 animate-pulse">
                <div className="h-4 w-24 rounded bg-muted" />
              </div>
              <div className="space-y-5 px-5 pb-5 animate-pulse">
                {Array.from({ length: fields }).map((_, j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3.5 w-28 rounded bg-muted" />
                    <div className="h-9 w-full rounded-lg bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      }
      aside={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pt-4 pb-3 animate-pulse">
            <div className="h-4 w-24 rounded bg-muted" />
          </div>
          <div className="flex items-center gap-4 px-5 pb-4 animate-pulse">
            <div className="size-16 rounded-full bg-muted" />
            <div className="space-y-2">
              <div className="h-4 w-36 rounded bg-muted" />
              <div className="h-3 w-48 rounded bg-muted" />
            </div>
          </div>
        </div>
      }
    />
  );
}

export function MyDetails() {
  const { brandDetails, fetchBrandDetails } = useContext(BrandContext);
  const { driverInstance, isTourOpen } = useDriver();
  const sections = useSections("profile", isTourOpen);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showProfileCropModal, setShowProfileCropModal] = useState(false);
  const [tempProfileSrc, setTempProfileSrc] = useState("");
  const [tempProfileFile, setTempProfileFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<ProfileForm>({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    timezone: "UTC",
    profileImage: "",
  });

  // Password lives in its own dialog; it is never part of the profile form.
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // The clock in the aside: the one place the timezone setting becomes real.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (driverInstance && isTourOpen) {
      driverInstance.highlight({
        element: "#profile-photo",
        popover: {
          side: "bottom",
          title: "Your photo",
          description: "This will be displayed on your profile.",
        },
      });
    }
  }, [driverInstance, isTourOpen]);

  const fromBrand = (): ProfileForm => ({
    firstName: brandDetails?.firstName || "",
    lastName: brandDetails?.lastName || "",
    email: brandDetails?.email || "",
    country: brandDetails?.country || "",
    timezone: brandDetails?.timezone || "UTC",
    profileImage: brandDetails?.profileImage || "",
  });

  useEffect(() => {
    if (brandDetails) {
      setFormData(fromBrand());
      setIsInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandDetails]);

  const hasChanges = useMemo(() => {
    if (!brandDetails) return false;
    return (
      formData.firstName !== (brandDetails.firstName || "") ||
      formData.lastName !== (brandDetails.lastName || "") ||
      formData.country !== (brandDetails.country || "") ||
      formData.timezone !== (brandDetails.timezone || "UTC") ||
      selectedImage !== null
    );
  }, [formData, brandDetails, selectedImage]);

  const handleImageSelect = async (file: File) => {
    try {
      const imageDataUrl = await readFile(file);
      setTempProfileFile(file);
      setTempProfileSrc(imageDataUrl);
      setShowProfileCropModal(true);
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Could not read that image");
    }
  };

  const handleProfileCropComplete = (croppedFile: File) => {
    if (formData.profileImage.startsWith("blob:")) URL.revokeObjectURL(formData.profileImage);
    setSelectedImage(croppedFile);
    setFormData((prev) => ({ ...prev, profileImage: URL.createObjectURL(croppedFile) }));
    setShowProfileCropModal(false);
    setTempProfileSrc("");
    setTempProfileFile(null);
  };

  const handleProfileCropCancel = () => {
    setShowProfileCropModal(false);
    setTempProfileSrc("");
    setTempProfileFile(null);
  };

  useEffect(() => {
    return () => {
      if (formData.profileImage.startsWith("blob:")) URL.revokeObjectURL(formData.profileImage);
    };
  }, [formData.profileImage]);

  const reset = () => {
    if (formData.profileImage.startsWith("blob:")) URL.revokeObjectURL(formData.profileImage);
    setFormData(fromBrand());
    setSelectedImage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setIsSaving(true);
    try {
      const imageUrl = selectedImage ? await storageService.uploadProfileImage(selectedImage) : undefined;
      await brandService.updatePersonalDetails({ ...formData, profileImage: imageUrl });
      await fetchBrandDetails();
      setSelectedImage(null);
      toast.success("Profile saved");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Could not save your profile");
    } finally {
      setIsSaving(false);
      if (driverInstance && isTourOpen && formData.profileImage.startsWith("blob:")) {
        driverInstance.moveNext();
      }
    }
  };

  const closePasswordDialog = () => {
    setPasswordOpen(false);
    setPasswordForm({ current: "", next: "", confirm: "" });
    setShowPasswords(false);
    setPasswordError(null);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("The new passwords do not match");
      return;
    }
    setChangingPassword(true);
    setPasswordError(null);
    try {
      await passwordService.changePassword(passwordForm.current, passwordForm.next, "brand");
      toast.success("Password changed");
      closePasswordDialog();
    } catch (err) {
      console.error("Failed to change password:", err);
      setPasswordError(err instanceof Error ? err.message : "Could not change the password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (isInitialLoading) return <DetailsSkeleton />;

  const name = [formData.firstName, formData.lastName].filter(Boolean).join(" ").trim();
  const countryCode = countries.find((c) => c.name === formData.country)?.code;
  const device = deviceZone();
  const zones =
    device && !TIMEZONES.some((t) => t.value === device)
      ? [...TIMEZONES, { value: device, label: zoneLabel(device) }]
      : TIMEZONES;
  const localTime = timeIn(formData.timezone, now);
  const deviceTime = device ? timeIn(device, now) : null;
  const deviceDiffers = Boolean(device && device !== formData.timezone && deviceTime !== localTime);

  return (
    <form onSubmit={handleSubmit}>
      <SettingsGrid
        form={
          <>
            <SettingsSection
              id="profile-section"
              title="Profile"
              open={sections.isOpen("profile")}
              onToggle={() => sections.toggle("profile")}
              bodyClassName="space-y-5"
            >
              <div id="SPF-profile-photo-field">
                <div id="profile-photo">
                  <ImageUploader
                    id="profile-photo-upload"
                    image={formData.profileImage}
                    onChange={handleImageSelect}
                    showPreview={false}
                    helpText="Your photo, PNG, JPG or WebP, up to 3MB"
                  />
                </div>
              </div>
              <div id="SPF-name-field">
                <FieldRow>
                  <Field label="First name" htmlFor="first-name">
                    <Input
                      id="first-name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Last name" htmlFor="last-name">
                    <Input
                      id="last-name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      autoComplete="family-name"
                    />
                  </Field>
                </FieldRow>
              </div>
            </SettingsSection>

            <SettingsSection
              id="location-section"
              title="Location"
              open={sections.isOpen("location")}
              onToggle={() => sections.toggle("location")}
              bodyClassName="space-y-5"
            >
              <Field id="SPF-country-field" label="Country" htmlFor="country">
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Choose a country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                id="SPF-timezone-field"
                label="Timezone"
                htmlFor="timezone"
                help={
                  <>
                    Reports, emails and payout dates use this time.
                    {deviceDiffers && device && (
                      <>
                        {" "}
                        Your device is on {zoneLabel(device)}, where it is {deviceTime}.{" "}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, timezone: device })}
                          className="font-medium text-foreground underline-offset-4 hover:underline"
                        >
                          Use it
                        </button>
                      </>
                    )}
                  </>
                }
              >
                <Select
                  value={formData.timezone}
                  onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                >
                  <SelectTrigger id="timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((zone) => (
                      <SelectItem key={zone.value} value={zone.value}>
                        {zone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </SettingsSection>

            <SettingsSection
              id="sign-in-section"
              title="Sign-in"
              open={sections.isOpen("sign-in")}
              onToggle={() => sections.toggle("sign-in")}
              bodyClassName="space-y-5"
            >
              <Field
                id="SPF-email-field"
                label="Email"
                htmlFor="email"
                help="You sign in with this address. Contact support to change it."
              >
                <Input id="email" type="email" value={formData.email} readOnly disabled />
              </Field>
              <div id="SPF-password-field" className="space-y-1.5">
                <Label className="text-foreground">Password</Label>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-input px-3 py-2">
                  <span className="text-sm tracking-widest text-muted-foreground">••••••••</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPasswordOpen(true)}>
                    Change password
                  </Button>
                </div>
              </div>
            </SettingsSection>
          </>
        }
        aside={
          <>
          <DashboardCard title="Your account" bodyClassName="px-5 pb-2">
            <div className="flex items-center gap-4 pb-3">
              <UserAvatar src={formData.profileImage || null} name={name} className="size-16 text-xl" />
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold text-foreground">{name || "Your name"}</div>
                <div className="truncate text-xs text-muted-foreground">{formData.email}</div>
              </div>
            </div>
            <AsideRow label="Company">{brandDetails?.companyName || "—"}</AsideRow>
            <AsideRow label="Country">
              {formData.country ? (
                <span className="inline-flex items-center gap-1.5">
                  {countryCode && <Flag code={countryCode} />}
                  {formData.country}
                </span>
              ) : (
                "—"
              )}
            </AsideRow>
            <AsideRow label="Local time">
              {localTime ? (
                <span className="tabular-nums">
                  {localTime}
                  <span className="text-muted-foreground"> · {zoneLabel(formData.timezone)}</span>
                </span>
              ) : (
                "—"
              )}
            </AsideRow>
            {brandDetails?.createdAt && (
              <AsideRow label="Member since">{formatDate(brandDetails.createdAt)}</AsideRow>
            )}
          </DashboardCard>
          <UnsavedBar visible={hasChanges} saving={isSaving} onCancel={reset} saveId="SPF-save-button" />
          </>
        }
      />

      {showProfileCropModal && tempProfileSrc && tempProfileFile && (
        <ImageCropModal
          imageSrc={tempProfileSrc}
          onClose={handleProfileCropCancel}
          onCropComplete={handleProfileCropComplete}
          originalFileName={tempProfileFile.name}
          aspectRatio={1}
          cropShape="round"
          title="Crop your photo"
          description="Drag to position, pinch or scroll to zoom"
        />
      )}

      <ActionDialog
        open={passwordOpen}
        onClose={closePasswordDialog}
        title="Change password"
        footer={
          <>
            <DialogTextButton onClick={closePasswordDialog} disabled={changingPassword}>
              Cancel
            </DialogTextButton>
            <Button type="submit" form="change-password-form" disabled={changingPassword}>
              Change password
            </Button>
          </>
        }
      >
        <form id="change-password-form" onSubmit={handlePasswordChange} className="space-y-4">
          {passwordError && <DialogNotice tone="error">{passwordError}</DialogNotice>}
          <Field label="Current password" htmlFor="current-password">
            <Input
              id="current-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.current}
              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
              autoComplete="current-password"
              required
            />
          </Field>
          <Field label="New password" htmlFor="new-password">
            <Input
              id="new-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label="Confirm new password" htmlFor="confirm-password">
            <Input
              id="confirm-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              autoComplete="new-password"
              required
            />
          </Field>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-passwords"
              checked={showPasswords}
              onCheckedChange={(checked) => setShowPasswords(checked === true)}
            />
            <Label htmlFor="show-passwords" className="font-normal text-foreground">
              Show passwords
            </Label>
          </div>
        </form>
      </ActionDialog>
    </form>
  );
}
